import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InsertEnterpriseRiskMutation,
  InsertEnterpriseRiskMutationVariables,
  Risk_Treatment_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertEnterpriseRiskDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

// The tRPC procedure input is a `Tier`-discriminated union (tier 1 forbids a
// parent; tiers 2/3 require one), so the flat GraphQL variables are narrowed
// into the matching union member in `mapInput`.
type InsertEnterpriseRiskTRPCInput = {
  Title: string;
  Description: string;
  Treatment: Risk_Treatment_Type_Enum | null;
  CustomAttributeData: CustomAttributeData | null;
} & (
  | { Tier: 1; ParentId: null }
  | { Tier: 2; ParentId: string }
  | { Tier: 3; ParentId: string }
);

export const useInsertEnterpriseRisk = createMutationHook({
  name: 'insertEnterpriseRisk',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.enterpriseRisk.insert.mutationOptions({ onSuccess }),
  mapInput: (
    variables: InsertEnterpriseRiskMutationVariables
  ): InsertEnterpriseRiskTRPCInput => {
    const base = {
      Title: variables.Title,
      Description: variables.Description ?? '',
      Treatment: variables.Treatment ?? null,
      CustomAttributeData: variables.CustomAttributeData ?? null,
    };

    if (variables.Tier === 1) {
      return { ...base, Tier: 1, ParentId: null };
    }

    if (!variables.ParentId) {
      throw new Error(
        'A parent enterprise risk is required for tier 2 and tier 3 risks'
      );
    }

    if (variables.Tier === 2) {
      return { ...base, Tier: 2, ParentId: variables.ParentId };
    }

    if (variables.Tier === 3) {
      return { ...base, Tier: 3, ParentId: variables.ParentId };
    }

    throw new Error(`Unsupported enterprise risk tier: ${variables.Tier}`);
  },
  invalidate: (trpc) => [
    trpc.frontend.enterpriseRisk.register.queryKey(),
    trpc.frontend.enterpriseRisk.getById.queryKey(),
    trpc.frontend.enterpriseRisk.getByTier.queryKey(),
  ],
  // Maps the tRPC create response (the full enterprise_risk row, which carries
  // the generated Id) to the GraphQL mutation shape.
  mapResponse: (result: { Id: string }): InsertEnterpriseRiskMutation => ({
    insertChildEnterpriseRisk: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertEnterpriseRiskDocument,
  graphqlVariables: (variables: InsertEnterpriseRiskMutationVariables) => ({
    ...variables,
  }),
  evictFields: ['enterprise_risk'],
  refetchQueries: [
    namedOperations.Query.getEnterpriseRiskById,
    namedOperations.Query.getEnterpriseRisksFlat,
    namedOperations.Query.getEnterpriseRisksByTier,
    namedOperations.Query.getEnterpriseRisks,
  ],
});
